import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    // Show/hide header based on scroll direction
    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    
    // Add background blur when scrolled
    setIsScrolled(currentScrollY > 20);
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (isMenuOpen) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.mobile-menu') && !e.target.closest('.menu-toggle')) {
          setIsMenuOpen(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMenuOpen]);

  return (
    <>
      <header 
        className={`
          fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out
          ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-gradient-to-r from-blue-600 to-blue-700'}
          ${isVisible ? 'translate-y-0' : '-translate-y-full'}
        `}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            
            {/* Logo Section */}
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <img
                  src="/images/logo-header.png"
                  alt="Faucon Bleu - Service automobile mobile"
                  className={`h-8 lg:h-12 w-auto transition-all 
                      ${isScrolled ? 'bg-[#1B78C7] p-1' :""}
                  duration-300`}
                  loading="eager"
                  width="160"
                  height="48"
                />
              </div>
              
              {/* Divider */}
              <div className={`w-px h-8 lg:h-12 transition-colors duration-300 ${
                isScrolled ? 'bg-gray-300' : 'bg-white/30'
              }`} />
              
              {/* Partnership Badge */}
              <div className="flex items-center space-x-2 lg:space-x-3">
                <div className="relative bg-white rounded-full">
                  <img
                    src="/images/shell.png"
                    alt="Shell - Partenaire officiel"
                    className="w-6 h-6 lg:w-10 lg:h-10 rounded-full ring-1 lg:ring-2 ring-white shadow-lg"
                    loading="lazy"
                  />
                </div>
                <div className={`transition-colors duration-300 ${
                  isScrolled ? 'text-blue-600' : 'text-white'
                }`}>
                  <div className="text-xs lg:text-sm font-semibold leading-tight">PARTENAIRE</div>
                  <div className="text-xs opacity-90  ">OFFICIEL</div>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6" role="navigation">
              <a
                href="https://reservation.faucon-bleu.ma/reservation"
                target="_blank"
                rel="noopener noreferrer"
                className={`
                  group flex items-center space-x-2 px-6 py-3 rounded-full font-semibold
                  transition-all duration-300 hover:scale-105 hover:shadow-lg
                  ${isScrolled 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-white/90 text-blue-600 hover:bg-white'
                  }
                `}
              >
                <MessageCircle className="w-4 h-4 transition-transform group-hover:rotate-12" />
                <span>Vidange à domicile</span>
              </a>
              
              <a
                href="https://business.faucon-bleu.ma"
                target="_blank"
                rel="noopener noreferrer"
                className={`
                  group flex items-center space-x-2 px-6 py-3 rounded-full font-semibold
                  transition-all duration-300 hover:scale-105 hover:shadow-lg
                  ${isScrolled 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-white/90 text-green-600 hover:bg-white'
                  }
                `}
              >
                <div className="w-4 h-4 transition-transform items-center group-hover:scale-110">🏢</div>
                <span>ENTREPRISE</span>
              </a>

              {/* Quick Contact */}
              <a
                href="tel:+212702094593"
                className={`
                  flex items-center space-x-2 px-4 py-2 rounded-full
                  transition-all duration-300 hover:scale-105
                  ${isScrolled 
                    ? 'text-blue-600 hover:bg-blue-50' 
                    : 'text-white hover:bg-white/20'
                  }
                `}
                title="Appeler directement"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">07 02 09 45 93</span>
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className={`
                lg:hidden p-2 rounded-lg transition-all duration-300 menu-toggle
                ${isScrolled ? 'text-blue-600 hover:bg-blue-50' : 'text-white hover:bg-white/20'}
              `}
              aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`
          lg:hidden mobile-menu absolute top-full left-0 right-0 
          transition-all duration-300 ease-in-out
          ${isMenuOpen 
            ? 'opacity-100 translate-y-0 pointer-events-auto' 
            : 'opacity-0 -translate-y-4 pointer-events-none'
          }
        `}>
          <div className="bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-xl">
            <div className="max-w-7xl mx-auto px-4 py-6">
              
              <div className="space-y-4">
                <a
                  href="https://reservation.faucon-bleu.ma/reservation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-3 w-full py-4 px-6 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Vidange à domicile</span>
                </a>
                
                <a
                  href="https://business.faucon-bleu.ma"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-3 w-full py-4 px-6 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="text-lg">🏢</span>
                  <span>ENTREPRISE</span>
                </a>

                <a
                  href="tel:+212702094593"
                  className="flex items-center justify-center space-x-3 w-full py-4 px-6 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Phone className="w-5 h-5" />
                  <span>Appeler: 07 02 09 45 93</span>
                </a>

                <a
                  href="https://wa.me/212702094593"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-3 w-full py-4 px-6 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer to prevent content from hiding behind fixed header */}
      <div className="h-16 lg:h-20" />
    </>
  );
};

export default Header;